import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { H5, PageLevelTableWrapper, TOAST_TYPE } from '@base'
import { Pill, Table, useTableReducer } from '@baseV2'
import { COLLECTION_STATUS, DRAWER_TYPES, MB_ROLES, collectionFilter, strings } from '@constants'
import { getAllPaymentReceivable, getCollectionByOrgId } from '@data'
import { toggleDrawer } from '@data/state/action/root'
import { displayAmount, showDateFormat } from '@utils'
import { showToast } from '@data/state/action'
import { useForceUpdate } from '@hooks'
import { Text } from '@styles'
import { useOutletContext } from 'react-router'

const EditCollectionWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
`

const AmountWapper = styled(H5)`
	font-weight: ${Text.BOLD_600};
`

// Status pill colours (same meaning as the old Chip colours): pending = blue, cancelled = red, paid = green,
// partially paid / rescheduled = orange.
const COLLECTION_STATUS_PILL_COLOR = {
	[COLLECTION_STATUS.PENDING]: 'info',
	[COLLECTION_STATUS.CANCELLED]: 'error',
	[COLLECTION_STATUS.PAID]: 'success',
	[COLLECTION_STATUS.PARTIALLY_PAID]: 'warning',
	[COLLECTION_STATUS.RESCHEDULED]: 'warning',
}

const CopySpan = styled.span`
	cursor: copy;
`

const Collections = ({ isSales, CollectionById = null, statusFilter = null }) => {
	const [state, dispatch, actions] = useTableReducer(collectionFilter)
	const [forceUpdate, update] = useOutletContext()
	const globalDispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	useEffect(() => {
		if (state.loading) return
		dispatch(actions.fetchTableDataProgress())
		if (statusFilter && CollectionById) {
			getCollectionByOrgId(state.query.page, CollectionById, statusFilter, state.query.filters)
				.then((response) => {
					dispatch(actions.fetchTableDataSuccess(response.data, response.count))
				})
				.catch((err) => dispatch(actions.fetchTableDataFailure(err)))
		} else {
			getAllPaymentReceivable(
				state.query.page,
				[MB_ROLES.SALES].includes(user?.role) ? { ...state.query.filters, managerFilter: user.user_id } : state.query.filters
			)
				.then((response) => {
					dispatch(actions.fetchTableDataSuccess(response.data, response.count))
				})
				.catch((err) => dispatch(actions.fetchTableDataFailure(err)))
		}
	}, [state.query, statusFilter, forceUpdate])

	const copyOrgId = (e, data) => {
		e.stopPropagation()
		navigator.clipboard.writeText(data?.user_details?.organisation_id)
		globalDispatch(
			showToast(true, strings(['organisation', 'id', 'copied']), {
				type: TOAST_TYPE.INFO,
			})
		)
	}

	const columns = useMemo(
		() => [
			{
				label: strings('client'),
				key: (data) => {
					return (
						<CopySpan onClick={(e) => copyOrgId(e, data)}>
							{data?.user_details?.organisation_name ? data?.user_details?.organisation_name : data?.user_details?.full_name}
						</CopySpan>
					)
				},
				mobile: true,
			},
			{
				label: strings(['amount', '/ ', 'remarks']),
				key: (data) => {
					if (!data) {
						return '-'
					}
					return (
						<>
							<div>
								<AmountWapper>{data?.payment_amount ? displayAmount(data?.payment_amount) : '-'}</AmountWapper>
							</div>
							<div>
								<small>{data?.remarks[0]?.amount ? displayAmount(data?.remarks[0]?.amount) : '-'}</small>
							</div>
						</>
					)
				},
				mobile: true,
			},
			{
				label: strings('expected', 'date'),
				key: (data) => showDateFormat(new Date(data.expected_payment_date)),
				mobile: true,
			},
			{
				label: strings(['manager', '/ ', 'region']),
				key: (data) => {
					if (!data) return '-'
					return (
						<>
							<div>
								<b>
									{data?.manager?.slice(-1)?.[0]?.full_name
										? data?.manager?.slice(-1)?.[0]?.full_name
										: data?.manager?.slice(-1)?.[0]?.user_id}{' '}
								</b>
							</div>
							<div>
								<small>{data?.region ? data?.region : '-'}</small>
							</div>
						</>
					)
				},
				mobile: true,
			},
			{
				label: strings(['message', '/ ', 'mode_of_payment']),
				key: (data) => {
					return (
						<>
							<div>
								<b>{data?.message ? data?.message : '-'}</b>
							</div>
							<div>
								<small>{data?.payment_mode ? data?.payment_mode : '-'}</small>
							</div>
						</>
					)
				},
				mobile: true,
			},
			{
				label: strings('status'),
				key: (data) => {
					return (
						<div>
							{data?.status ? (
								<Pill color={COLLECTION_STATUS_PILL_COLOR[data?.status] ?? 'warning'} variant='subtle' round>
									{data?.status}
								</Pill>
							) : (
								'-'
							)}
						</div>
					)
				},
				mobile: true,
			},
			// Paginated table: client-side sort would only reorder the current page, so keep all columns non-sortable.
		].map((col) => ({ ...col, sortable: false })),
		[]
	)

	const copyReceivableId = (e, row) => {
		e.stopPropagation()
		navigator.clipboard.writeText(row?.receivable_id)
		globalDispatch(
			showToast(true, strings(['receivable', 'id', 'copied']), {
				type: TOAST_TYPE.INFO,
			})
		)
	}

	const rowClickHandler = (row) => {
		if (row?.remarks == null || row?.remarks?.length === 0) {
			globalDispatch(toggleDrawer(true, DRAWER_TYPES.ADD_COLLECTION, { paymentReceivables: row, isAddRemark: true, update }))
		} else {
			globalDispatch(
				toggleDrawer(true, DRAWER_TYPES.COLLECTION_JOURNEY_V2, {
					row,
					update,
					label: (
						<div>
							<span>{`${row?.user_details?.organisation_name || row?.user_details?.organisation_id} `}</span>
							<CopySpan onClick={(e) => copyReceivableId(e, row)}>{`(${row?.receivable_id})`}</CopySpan>
						</div>
					),
				})
			)
		}
	}

	const changePageHandler = (next = true) => {
		dispatch(actions.changePage(next))
	}

	return (
		<PageLevelTableWrapper>
			<EditCollectionWrapper>
				<Table
					fullHeight
					data={state.data}
					columns={columns}
					loading={state.loading}
					onRowClick={rowClickHandler}
					rowsToDisplay={10}
					totalItemCount={state.total_count}
					page={state.query.page}
					onPageChange={changePageHandler}
				/>
			</EditCollectionWrapper>
		</PageLevelTableWrapper>
	)
}

export default Collections
